import React, { useState } from 'react';
import { useTheme, TOKENS } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

function Contact() {
  const { theme } = useTheme();
  const c = TOKENS[theme];
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setStatus('error:Please fill in all fields');
      setTimeout(() => setStatus(''), 3000);
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, timestamp: new Date().toISOString() }),
      });
      const data = await response.json();
      if (response.ok) {
        setStatus("success:Message sent! We'll get back to you soon.");
        setForm({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus(''), 5000);
      } else {
        setStatus(`error:${data.error || 'Failed to send message'}`);
        setTimeout(() => setStatus(''), 3000);
      }
    } catch (err) {
      setStatus(`error:${err.message}`);
      setTimeout(() => setStatus(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const isError = status.startsWith('error:');
  const statusMessage = status.replace(/^(error:|success:)/, '');

  const inputStyle = {
    width: '100%', padding: '12px 14px',
    background: c.card, border: `1px solid ${c.border}`,
    borderRadius: '7px', color: c.text, fontSize: '14px',
    fontFamily: "'Inter', -apple-system, sans-serif",
    transition: 'border-color 0.15s, background 0.25s',
    boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif", transition: 'background 0.25s, color 0.25s' }}>
      <SEO title="Contact — TrailCode" description="Get in touch with the TrailCode team." />
      <Navbar />

      <div style={{ textAlign: 'center', padding: '60px 20px 40px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '12px', letterSpacing: '-0.5px', color: c.text }}>Get in Touch</h1>
        <p style={{ color: c.secondary, fontSize: '16px', maxWidth: '480px', margin: '0 auto' }}>
          Have a question, bug report or feature request? We'd love to hear from you.
        </p>
      </div>

      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '0 40px 80px' }}>
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', padding: '32px', transition: 'background 0.25s, border-color 0.25s' }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.secondary, marginBottom: '8px' }}>Name *</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Your name" disabled={loading} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.secondary, marginBottom: '8px' }}>Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" disabled={loading} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.secondary, marginBottom: '8px' }}>Subject *</label>
              <select name="subject" value={form.subject} onChange={handleChange} disabled={loading} style={inputStyle}>
                <option value="">Select a subject...</option>
                <option value="Bug Report">🐛 Bug Report</option>
                <option value="Feature Request">✨ Feature Request</option>
                <option value="Question">❓ Question</option>
                <option value="Feedback">💬 Feedback</option>
                <option value="Partnership">🤝 Partnership</option>
                <option value="Other">📋 Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: c.secondary, marginBottom: '8px' }}>Message *</label>
              <textarea name="message" value={form.message} onChange={handleChange} placeholder="Your message here..." rows="6" disabled={loading} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }} />
            </div>

            {status && (
              <div style={{
                background: isError ? (theme === 'light' ? '#fee2e2' : '#1a0808') : (theme === 'light' ? '#d1fae5' : '#051a10'),
                border: `1px solid ${isError ? c.error : c.success}`,
                color: isError ? c.error : c.success,
                padding: '12px 14px', borderRadius: '7px', fontSize: '13px',
              }}>
                {isError ? '❌ ' : '✅ '}{statusMessage}
              </div>
            )}

            <button type="submit" disabled={loading} style={{ background: loading ? c.muted : c.accent, color: theme === 'dark' ? c.bg : '#fff', border: 'none', padding: '12px 24px', borderRadius: '7px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', marginTop: '8px', opacity: loading ? 0.6 : 1 }}>
              {loading ? '⏳ Sending...' : 'Send Message'}
            </button>
          </form>

          <div style={{ marginTop: '28px', paddingTop: '28px', borderTop: `1px solid ${c.border}` }}>
            <p style={{ fontSize: '13px', color: c.secondary, marginBottom: '12px', fontWeight: '500' }}>Or reach out directly:</p>
            <p style={{ fontSize: '13px', color: c.text, margin: '8px 0' }}>
              📧 <a href="mailto:hello@trailcode.org" style={{ color: c.accent, fontWeight: '500', textDecoration: 'none' }}>hello@trailcode.org</a>
            </p>
            <p style={{ fontSize: '13px', color: c.text, margin: '8px 0' }}>
              🐛 GitHub: <a href="https://github.com/TrailCode2026" target="_blank" rel="noreferrer" style={{ color: c.accent, fontWeight: '500', textDecoration: 'none' }}>TrailCode2026</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;